import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild } from '@angular/core';
import { TreeService } from '@schoolbelle/api/group-to-group';
import { LetterTargetService } from '@schoolbelle/api/letter-target';
import { of, Subscription } from 'rxjs';
import { GroupMemberService, convertFacultylistToTableData, GroupMember } from '@schoolbelle/api/group-member';
import { Table, Row } from '@schoolbelle/common/table';
import {get, maxBy } from 'lodash-es';
import { TableSearchService } from '../table-search/table-search.service';
import { switchMap } from 'rxjs/operators';
import { VirtualScrollerComponent } from 'ngx-virtual-scroller';

@Component({
  selector: 'faculty-tab',
  templateUrl: './faculty-tab.component.html',
  styleUrls: ['./faculty-tab.component.scss'],
  changeDetection:ChangeDetectionStrategy.OnPush,
})
export class SelectLetterTargetsFacultyTabComponent implements OnInit {
  @ViewChild('scroll') scroll:VirtualScrollerComponent;
  table = new Table();
  rows:Row[] = [];
  subscription = new Subscription();

  constructor(
    private tree:TreeService,
    private member:GroupMemberService,
    private letterTarget:LetterTargetService,
    private tableSearch:TableSearchService,
    private cdRef:ChangeDetectorRef,
  ) { }

  ngOnInit() {
    this.updateTableContent(this.tree.root_group_id);
    this.updateTableCheckedArea(this.tree.root_group_id);
    this.subscription.add(
      this.letterTarget.onChange
      .subscribe(()=>{
        this.updateTableCheckedArea(this.tree.root_group_id);
      })
    )
    this.subscription.add(
      this.tableSearch.onSelect.subscribe(member=>{
        this.goToMember(member);
      })
    )    

  }
  onSelectionChange ($event) {
    this.updateTargetScopes(this.tree.root_group_id);
  } 
  goToMember (member:GroupMember) {
    this.table.cells.filter(c=>c.matched).forEach(c=>c.matched = false);
    const group = this.tree.root.find(t=>t.group_id === member.group_id)
    if (!group) throw new Error('Group not found');
  
    const cell = this.table.cells.find(c=>{
      const m = c.get('member');
      return m && m.member_id === member.member_id;
    });

    if (!cell) throw new Error('Cell not found');
    const i = cell.y;
    cell.matched = true;
    const {startIndex, endIndex} = this.scroll.viewPortInfo;
    const size = endIndex - startIndex;
    const alignToBeginning = this.table.y - size < i;
    this.scroll.scrollToIndex(i, alignToBeginning);
    this.cdRef.detectChanges();
  }
  ngOnDestroy () {
    this.subscription.unsubscribe()
  }

  private updateTableContent (group_id) {
    const facultylist = this.member.filter({group_id, member_type:'faculty'});
    const data = convertFacultylistToTableData(facultylist);
    this.table.x = 3;
    this.table.y = get(maxBy(facultylist, 'member_num'), 'member_num');
    this.table.load(data);
    this.rows = this.table.rows.filter(row=>row.cells[1].val);    
  }
  private updateTableCheckedArea (group_id) {
    // const targetScopes:string = this.letterTarget.getScopes('faculty_', group_id);
    const targetScopes:string = this.letterTarget.getScopes(group_id);
    const tableCheckedArea = this.convertLetterTargetScopesToTableArea('faculty_', targetScopes, group_id);
    if (this.table.selectedArea !== tableCheckedArea) {
      this.table.selectedArea = tableCheckedArea;
      this.cdRef.detectChanges()
    }  
  }
  private updateTargetScopes (group_id) {
    const tableCheckedArea = this.table.selectedArea;
    const letterTargetScope = this.convertTableAreaToLetterTargetScopes('faculty_', tableCheckedArea, group_id);
    this.letterTarget.setScopes(group_id, letterTargetScope, 'up');
  }
  private convertLetterTargetScopesToTableArea (tablename, scopes, group_id) {
    return scopes.split(',').filter(s=>s)
    .map(scope => scope.replace(tablename, ''))
    .filter(scope=>!scope.match(/col_[^1]/))
    .map(scope=>{
      return scope.replace(/col_([\d]+)/, (matched, n)=>{
        switch(n) {
          case '1':
          return `col_3`;
          default :
          return matched;
        }
      })
    })    
    .map(scope=>{
      // from : all, col_num, row_member_name, col_num_of_row_member_name
      // to   : all, col_num, row_num, col_num_of_row_num
      return scope.replace(/^(col_[\d]+)?(_of_)?row_(.+)$/, (matched, col_num, conj, URIComponentEncoded_member_name)=>{
        const m = this.member.search({group_id, member_types:['faculty', 'student'], member_name:decodeURIComponent(URIComponentEncoded_member_name)})[0];
        return m ? `${col_num || ''}${conj || ''}row_${m.member_num}` : '';
      })
    })
    .join(',')    
  }
  private convertTableAreaToLetterTargetScopes (tablename, areas, group_id:number) {
    return areas.split(',').filter(a=>a)
    .filter(scope=>!scope.match(/col_[^3]/))
    .map(scope=>{
      return scope.replace(/col_([\d]+)/, (matched, n)=>{
        switch(n) {
          case '3':
          return `col_1`;
          default :
          return matched;
        }
      })
    })    
    .map(scope=>{
      // from : all, col_num, row_num, col_num_of_row_num
      // to   : all, col_num, row_member_name, col_num_of_row_member_name
      return scope.replace(/^(col_[\d]+)?(_of_)?row_([\d]+)$/, (matched, col_num, conj, member_num)=>{
        const m = this.member.search({group_id, member_types:['student', 'faculty'], member_num:Number(member_num)})[0];
        return m ? `${col_num || ''}${conj || ''}row_${encodeURIComponent(m.member_name)}`:'';
      });
    })
    .map(area=>tablename + area)
    .join(',')    
  }
  trackById (index, row) {
    return row.id;
  } 
  toggleTable () {
    this.table.toggleAreaByXY(undefined, undefined);
    this.toggleNumNameCols();
  }
  isColumnSelected (i:number) {
    const col = this.table.getColumn(i);
    return col && col.selected && col.selected[0] === col;
  }  
  isTableSelected () {
    const table = this.table;
    return table && table.selected && table.selected[0] === table;
  }  
  private toggleNumNameCols () {
    const area = this.table.selectedArea.split(',');
    if (
      area.includes('col_3')
    ) {
      if (!area.includes('col_1')) this.table.toggleAreaByXY(0, undefined);
      if (!area.includes('col_2')) this.table.toggleAreaByXY(1, undefined);
    }
    else {
      if (area.includes('col_1')) this.table.toggleAreaByXY(0, undefined);
      if (area.includes('col_2')) this.table.toggleAreaByXY(1, undefined);

    }
  }
}
